import React, { useState } from 'react';
import { Box, Typography, Button, Card, CardContent, Grid, List, ListItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent } from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import LoginForm from './LoginForm';

const PricingSection = () => {
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const planFeatures = {
    basic: [
      { title: "Unlimited Invoices", description: "Create and manage unlimited invoices for all your dental practices" },
      { title: "Monthly Invoicing", description: "Generate and send invoices on a monthly cycle" },
      { title: "PDF Generation", description: "Professional PDF invoices with your details auto-filled" },
      { title: "Practice Management", description: "Manage multiple dental practices with ease" },
      { title: "Secure Data Storage", description: "Enterprise-grade security with encrypted data storage" },
      { title: "Basic Support", description: "Email support within 24 hours" },
      { title: "Location-Based Job Search - Beta", description: "Find opportunities within your preferred travel distance" }
    ],
    pro: [
      { title: "Everything in Basic", description: "All features from the Basic plan included" },
      { title: "Daily Work Log", description: "Track daily work, earnings, and deductions efficiently" },
      { title: "Revenue Reports", description: "Detailed financial analytics and income tracking" },
      { title: "Invoice History", description: "Complete searchable history of all invoices" },
      { title: "Expense Management", description: "Track expenses and mileage to maximize deductions" },
      { title: "Tax Estimations", description: "Automated tax liability estimations based on income & expenses" },
      { title: "Priority Support", description: "Premium support with faster response times" }
    ]
  };

  const handleTrialClick = (plan) => {
    setSelectedPlan(plan);
    setShowLoginPopup(true);
  };

  return (
    <>
      <Box sx={{ py: 4, px: 2 }}>
        {/* Title */}
        <Typography 
          variant="h4" 
          component="h2" 
          align="center" 
          sx={{ color: '#A78BFA', mb: 4, fontWeight: 'bold' }}
        >
          Choose Your Plan
        </Typography>
        
        {/* Container for pricing cards with fixed width to ensure uniformity */}
        <Box sx={{ 
          maxWidth: '960px', 
          mx: 'auto', 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: { xs: 3, md: 3 }
        }}>
          {/* Basic Plan Card */}
          <Box sx={{ 
            flex: '1 1 0px', // This ensures both boxes have exactly the same width
            display: 'flex', 
            flexDirection: 'column' 
          }}>
            <Card sx={{ 
              bgcolor: '#2A2A3E',
              display: 'flex',
              flexDirection: 'column',
              height: '100%', // Full height of parent
              p: 2,
              borderRadius: 2,
              border: '1px solid rgba(138, 43, 226, 0.2)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}>
              {/* Spacer to account for "Best Value" badge */}
              <Box sx={{ height: 12, visibility: 'hidden' }}></Box>
              
              <CardContent sx={{ 
                p: 1, 
                pb: 1, 
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Plan Name */}
                <Typography variant="h6" component="h3" sx={{ color: '#A78BFA', fontWeight: 'bold' }}>
                  Basic Plan
                </Typography>
                
                {/* Price */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                    £25<span style={{ fontSize: '0.8rem' }}>/year</span>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    (£2.08 / month)
                  </Typography>
                </Box>
                
                {/* Trial */}
                <Typography sx={{ color: '#A78BFA', fontSize: '0.8rem', mt: 0.5 }}>
                  60-day free trial + 12 months
                </Typography>

                {/* Features List */}
                <List sx={{ 
                  my: 2, 
                  flexGrow: 1, 
                  py: 0,
                  minHeight: '240px', // Fixed height to ensure uniformity
                }}>
                  {planFeatures.basic.map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28, color: '#9370DB' }}>
                        <CheckIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={<Typography sx={{ fontWeight: 'medium', color: 'white', fontSize: '0.9rem' }}>{feature.title}</Typography>} 
                        secondary={<Typography sx={{ fontSize: '0.8rem', color: 'grey.400' }}>{feature.description}</Typography>} 
                        sx={{ my: 0 }}
                      />
                    </ListItem>
                  ))}
                </List>
                
                {/* Button + Note Container */}
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexDirection: 'column' }}>
                  <Button 
                    variant="contained" 
                    onClick={() => handleTrialClick('Basic')}
                    fullWidth
                    sx={{ 
                      bgcolor: '#8A2BE2', 
                      '&:hover': { bgcolor: '#6A1B9A' },
                      py: 0.75,
                      fontSize: '0.9rem',
                      height: '36px'
                    }}
                  >
                    Start Free Trial
                  </Button>
                  <Box sx={{ height: '20px', mt: 1 }} /> {/* Empty space with fixed height */}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Pro Plan Card */}
          <Box sx={{ 
            flex: '1 1 0px', // This ensures both boxes have exactly the same width
            display: 'flex', 
            flexDirection: 'column' 
          }}>
            <Card sx={{ 
              bgcolor: '#2A2E3E',
              display: 'flex',
              flexDirection: 'column',
              height: '100%', // Full height of parent
              p: 2,
              borderRadius: 2,
              border: '2px solid #8A2BE2',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              position: 'relative',
              overflow: 'visible'
            }}>
              {/* Best Value Label */}
              <Box sx={{ 
                position: 'absolute', 
                top: -12,
                left: '50%', 
                transform: 'translateX(-50%)', 
                bgcolor: '#4ade80', 
                color: '#0f172a', 
                fontSize: '0.7rem',
                fontWeight: 'bold', 
                px: 1.5,
                py: 0.3,
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 1, 
                whiteSpace: 'nowrap'
              }}>
                Best Value
              </Box>
              
              <CardContent sx={{ 
                p: 1, 
                pb: 1, 
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Plan Name */}
                <Typography variant="h6" component="h3" sx={{ color: '#A78BFA', fontWeight: 'bold' }}>
                  Pro Plan
                </Typography>
                
                {/* Price */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                    £50<span style={{ fontSize: '0.8rem' }}>/year</span>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    (£4.17 / month)
                  </Typography>
                </Box>
                
                {/* Trial */}
                <Typography sx={{ color: '#A78BFA', fontSize: '0.8rem', mt: 0.5 }}>
                  60-day free trial + 12 months
                </Typography>

                {/* Features List */}
                <List sx={{ 
                  my: 2, 
                  flexGrow: 1, 
                  py: 0,
                  minHeight: '240px', // Fixed height to ensure uniformity
                }}>
                  {planFeatures.pro.map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <StarIcon 
                          fontSize="small" 
                          sx={{ 
                            color: '#4ade80',
                            fontSize: '1.1rem'
                          }} 
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={<Typography sx={{ fontWeight: 'medium', color: 'white', fontSize: '0.9rem' }}>{feature.title}</Typography>} 
                        secondary={<Typography sx={{ fontSize: '0.8rem', color: 'grey.400' }}>{feature.description}</Typography>} 
                        sx={{ my: 0 }}
                      />
                    </ListItem>
                  ))}
                </List>
                
                {/* Button + Note */}
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexDirection: 'column' }}>
                  <Button 
                    variant="contained" 
                    onClick={() => handleTrialClick('Pro')}
                    fullWidth
                    sx={{ 
                      bgcolor: '#4ade80', 
                      color: '#0f172a', 
                      '&:hover': { bgcolor: '#22c55e' },
                      py: 0.75,
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      height: '36px'
                    }}
                  >
                    Start Pro Trial
                  </Button>
                  <Typography 
                    variant="body2" 
                    align="center" 
                    sx={{ color: 'grey.400', mt: 1, fontSize: '0.8rem', height: '20px' }}
                  >
                    No credit card required for trial.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Annual billing note */}
        <Typography sx={{ textAlign: 'center', fontSize: '0.8rem', color: 'grey.400', mt: 2 }}>
          All plans are billed annually. Prices shown per month are for comparison.
        </Typography>
      </Box>
      
      {/* Plan selection login dialog */}
      <Dialog 
        open={showLoginPopup} 
        onClose={() => setShowLoginPopup(false)}
        PaperProps={{
          sx: {
            bgcolor: '#2A2A3E',
            border: '1px solid #8A2BE2',
            borderRadius: 2,
            maxWidth: 'md'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem', 
          fontWeight: 'bold', 
          color: '#A78BFA', 
          textAlign: 'center'
        }}>
          {selectedPlan ? `Start Your ${selectedPlan} Trial` : "Sign In"}
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Typography align="center" sx={{ color: 'grey.300', mb: 3 }}>
            {selectedPlan 
              ? `Sign in to start your 60-day free ${selectedPlan} trial. Your subscription begins after the trial ends.` 
              : "Sign in to access your account"}
          </Typography>
          
          <LoginForm />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PricingSection;
